import * as React from 'react'
import {
  readStoredPreference,
  storePreference,
  systemTheme,
  type ResolvedTheme,
  type ThemePreference,
} from './theme'

interface ThemeContextValue {
  preference: ThemePreference
  /** What is actually showing (a page that forces a theme reports it). */
  resolvedTheme: ResolvedTheme
  setPreference: (preference: ThemePreference) => void
  /** Force a theme while the caller is mounted; returns the release function. */
  lockTheme: (theme: ResolvedTheme) => () => void
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null)

/**
 * Light / dark / system theme for the dashboard and ops. Puts `.dark` on
 * <html> so portaled layers (dialogs, menus, toasts) theme too. Signer and
 * answerer pages force theirs with `useForcedTheme`: light, or the theme an
 * embed's host asks for. The latest page to force a theme wins.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // `setPreference` (below) also persists the choice, so the raw setter is named for the state.
  const [storedPreference, setStoredPreference] = React.useState<ThemePreference>(readStoredPreference)
  const [system, setSystem] = React.useState<ResolvedTheme>(systemTheme)
  const [locks, setLocks] = React.useState<{ id: number; theme: ResolvedTheme }[]>([])
  const nextLockRef = React.useRef(0)

  React.useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (): void => setSystem(query.matches ? 'dark' : 'light')
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  const chosen: ResolvedTheme = storedPreference === 'system' ? system : storedPreference
  const resolvedTheme: ResolvedTheme = locks.at(-1)?.theme ?? chosen

  React.useLayoutEffect(() => {
    document.documentElement.classList.toggle('dark', resolvedTheme === 'dark')
  }, [resolvedTheme])

  const setPreference = React.useCallback((next: ThemePreference): void => {
    storePreference(next)
    setStoredPreference(next)
  }, [])

  const lockTheme = React.useCallback((theme: ResolvedTheme): (() => void) => {
    nextLockRef.current += 1
    const id = nextLockRef.current
    setLocks((current) => [...current, { id, theme }])
    return () => setLocks((current) => current.filter((lock) => lock.id !== id))
  }, [])

  const value: ThemeContextValue = { preference: storedPreference, resolvedTheme, setPreference, lockTheme }
  return <ThemeContext value={value}>{children}</ThemeContext>
}

export function useTheme(): ThemeContextValue {
  const context = React.use(ThemeContext)
  if (context === null) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

/** Shows `theme` while mounted, whatever the person's preference (signer and answerer pages). */
export function useForcedTheme(theme: ResolvedTheme): void {
  const { lockTheme } = useTheme()
  React.useLayoutEffect(() => lockTheme(theme), [lockTheme, theme])
}

/** Keeps the page light while mounted (signer and answerer pages). */
export function useLightOnly(): void {
  useForcedTheme('light')
}

/**
 * The class that keeps paper light on a dark page: documents and signature
 * pads (drawn ink must stay dark on the white document it's stamped on).
 * Undefined on a light page, which needs nothing.
 */
export function usePaper(): string | undefined {
  return useResolvedTheme() === 'dark' ? 'ds-light' : undefined
}

/** What's showing; light outside a ThemeProvider (a component rendered on its own). */
export function useResolvedTheme(): ResolvedTheme {
  return React.use(ThemeContext)?.resolvedTheme ?? 'light'
}
